import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import Thumbnail from './Thumbnail';

const getTimeeventSourceName = (eventsource) => {
  const source = String(eventsource || '').trim();
  if (!source) {
    return '';
  }

  return source.split(/[\\/]/).pop() || source;
};

const getCompactTagValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }

  return String(value).trim();
};

const TimeEventCompactPreview = ({
  timeevent,
  dense = false,
  showSourcePath = true,
  maxVisibleTags = 4,
  thumbnailWidth,
  thumbnailHeight,
  sx,
}) => {
  const event = timeevent || {};
  const source = String(event.eventsource || '').trim();
  const sourceName = getTimeeventSourceName(source);
  const eventId = event.id ?? event.timeevent_id;
  const allTags = (Array.isArray(event.customFields) ? event.customFields : [])
    .filter((tag) => tag && String(tag.variable || '').trim());
  const tags = allTags.slice(0, maxVisibleTags);
  const hiddenTagCount = Math.max(0, allTags.length - tags.length);
  const resolvedThumbnailWidth = thumbnailWidth ?? (dense ? 48 : 62);
  const resolvedThumbnailHeight = thumbnailHeight ?? (dense ? 58 : 76);
  const fallbackLabel = event.label || (eventId !== undefined ? `#${eventId}` : 'Time event');

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        gap: dense ? 0.9 : 1.25,
        width: '100%',
        minWidth: 0,
        py: dense ? 0.25 : 0.5,
        ...sx,
      }}
    >
      <Box
        sx={{
          width: resolvedThumbnailWidth,
          height: resolvedThumbnailHeight,
          flexShrink: 0,
          pointerEvents: 'none',
          '& > *': { width: '100%', height: '100%' },
        }}
      >
        <Thumbnail
          timeevent={event}
          sx={{
            width: '100%',
            height: '100%',
            borderWidth: dense ? '1px' : '2px',
            borderRadius: dense ? '7px' : '9px',
          }}
        />
      </Box>

      <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: dense ? 0.2 : 0.35 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          {eventId !== undefined && (
            <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.2, flexShrink: 0 }}>
              #{eventId}
            </Typography>
          )}
          {event.eventtime && (
            <Typography
              variant="caption"
              sx={{ color: '#64748b', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {event.eventtime}
            </Typography>
          )}
        </Box>

        <Typography
          variant="body2"
          title={source || fallbackLabel}
          sx={{
            color: '#334155',
            fontWeight: 650,
            lineHeight: 1.25,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {sourceName || fallbackLabel}
        </Typography>

        {showSourcePath && source && sourceName !== source && (
          <Typography
            variant="caption"
            title={source}
            sx={{
              color: '#64748b',
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {source}
          </Typography>
        )}

        {tags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: dense ? 0.05 : 0.15, minWidth: 0 }}>
            {tags.map((tag, index) => {
              const variable = String(tag.variable).trim();
              const value = getCompactTagValue(tag.reply ?? tag.response);
              const label = value ? `${variable}: ${value}` : variable;

              return (
                <Chip
                  key={`${variable}-${tag.id || index}`}
                  label={label}
                  title={label}
                  size="small"
                  sx={{
                    height: dense ? 19 : 21,
                    maxWidth: { xs: 180, sm: 240 },
                    backgroundColor: 'rgba(87, 87, 176, 0.08)',
                    border: '1px solid rgba(87, 87, 176, 0.18)',
                    color: '#4545a0',
                    fontSize: dense ? '0.62rem' : '0.67rem',
                    fontWeight: 650,
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      px: 0.75,
                    },
                  }}
                />
              );
            })}
            {hiddenTagCount > 0 && (
              <Chip
                label={`+${hiddenTagCount}`}
                size="small"
                sx={{
                  height: dense ? 19 : 21,
                  backgroundColor: '#f1f5f9',
                  color: '#64748b',
                  fontSize: dense ? '0.62rem' : '0.67rem',
                  fontWeight: 700,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(TimeEventCompactPreview);
